import { asyncify, each } from 'async';
import { create, StateCreator } from 'zustand';

import {
  getStorkeDataFromUrl as restGetStorkeDataFromUrl,
  getTtsAudios as restGetTtsAudios,
} from '../../api/Rest/files';
import { translateLanguage as restTranslateLanguage } from '../../api/Rest/language';
import { TimeToSecond } from '../../constants/general';
import { FileType, TtsLocale } from '../../models/Base';
import {
  Audio,
  ConcreteFileDownload,
  File,
  FileDownloadFailure,
  FileDownloadFailureJson,
  FileDownloadResult,
  FileStoreAction,
  FileStoreState,
  GetFileParams,
  getUrlToGetFile,
  GetUrlToGetFileData,
  Icon,
  Image,
  ResourceImage,
  Stroke,
  TtsAudio,
  Video,
} from '../../models/FileStore';
import { StoreLifecycle, StoreLifecycleAction } from '../../models/Store';
import { FirstParameter } from '../../models/Utils';

type Store = StoreLifecycleAction & FileStoreState & FileStoreAction;

const lifecycle = new StoreLifecycle('FileStore');
lifecycle.finishHydration();

const initializer: StateCreator<Store, [], []> = (set, get) => {
  const initialize = lifecycle.createInitialize(
    () => {
      // do nothing
    },
    () => set((state) => ({ ...state })),
  );

  const shutdown = lifecycle.createShutdown(
    () => {
      // clear data
      set({
        fileDownload: {},
      });
    },
    () => set((state) => ({ ...state })),
  );

  const updateFileDownload: FileStoreAction['updateFileDownload'] = <
    F extends File,
  >(
    params: FileDownloadResult<F> | FileDownloadFailureJson,
  ) => {
    set((state) => {
      if ('id' in params) {
        return {
          fileDownload: {
            ...state.fileDownload,
            [params.id]: new FileDownloadFailure(params),
          },
        };
      }

      const id = ConcreteFileDownload.getId(params.file);
      const oldFileDownload = state.fileDownload[id] as ConcreteFileDownload<F>;
      const fileDownload =
        !oldFileDownload || oldFileDownload.isFailed
          ? new ConcreteFileDownload<F>(params.file)
          : oldFileDownload;
      fileDownload.update(params);
      return {
        fileDownload: {
          ...state.fileDownload,
          [id]: fileDownload,
        },
      };
    });
  };

  const getFileDownload: FileStoreAction['getFileDownload'] = <
    F extends File,
    T extends boolean = false,
  >(
    fileOrId: string | FirstParameter<typeof ConcreteFileDownload.getId<F>>,
    // TODO: add allowError back
    // allowError = false,
  ) => {
    const id: string =
      typeof fileOrId === 'string'
        ? fileOrId
        : ConcreteFileDownload.getId(fileOrId);
    const fileDownload = get().fileDownload[id];
    // if (allowError) {
    //   return fileDownload;
    // } else
    {
      return (fileDownload?.isFailed ? undefined : fileDownload) as ReturnType<
        typeof getFileDownload<F, T>
      >;
    }
  };

  const handlerGetFileUrl = async <F extends File>(
    params: GetFileParams<F>,
    signal: AbortSignal,
  ) => {
    const id = ConcreteFileDownload.getId<F>(params);
    // const state: GetFileUrlState = { id, version: params.version };

    // phase 1: get url
    // if (
    //   oldState?.url === undefined ||
    //   (oldState?.expiredAt ?? 0) <= Date.now()
    // ) {
    // remove hash if not the same
    if (params.hash) {
      const fileDownload = getFileDownload(id);
      const version = fileDownload?.getVersion(params.version);
      if (!version?.isPathDownloaded || version.isExpired(params.hash)) {
        params.hash = undefined;
      }
    }

    const res = await getUrlToGetFile<F>(params, signal);
    // update(state, 0.25);

    if (res.ok) {
      let file: FileDownloadResult<F>['file'];
      if ('attachmentType' in params) {
        /*eslint @typescript-eslint/no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
        const { version, ...attachment } = params;
        file = attachment as F;
      } else {
        switch (params.fileType) {
          case FileType.Image:
            file = (res.data as GetUrlToGetFileData<Image>).image as F;
            break;
          case FileType.Icon:
            file = (res.data as GetUrlToGetFileData<Icon>).icon as F;
            break;
          case FileType.Audio:
            file = (res.data as GetUrlToGetFileData<Audio>).audio as F;
            break;
          case FileType.Video:
            file = (res.data as GetUrlToGetFileData<Video>).video as F;
            break;
          case FileType.ResourceImage:
            file = (res.data as GetUrlToGetFileData<ResourceImage>)
              .resourceImage as F;
            break;
          case FileType.Stroke:
            file = (res.data as GetUrlToGetFileData<Stroke>).stroke as F;
            break;
          case FileType.TtsAudio:
            file = (res.data as GetUrlToGetFileData<TtsAudio>).ttsAudio as F;
            break;
        }
      }

      // update the file download with latest requested response
      const expiredAt = Date.now() + TimeToSecond.Hour * 1000;
      const url = res.data.url;

      updateFileDownload({
        file,
        version: params.version,
        expiredAt,
        url: url,
        path: null,
        error: null,
      });

      // state.expiredAt = expiredAt;
      // state.url = url;
      // update(state, 0.5);
    } else {
      let error: Error | undefined;
      if (res.statusCode === 304) {
        // file not modified
        const fileDownload = getFileDownload(id);
        if (fileDownload === undefined) {
          error = new Error(`File data is not exist - ${id}`);
        } else if (fileDownload.isFailed) {
          error = new Error(`File has error - ${id}`);
        } else {
          const version = fileDownload.getVersion(params.version);
          if (!version?.url) {
            error = new Error(
              `Invalid version - ${params.version ?? 'default'}`,
            );
          } else {
            // update the file download with latest requested response
            const expiredAt = Date.now() + TimeToSecond.Hour * 1000;

            updateFileDownload({
              file: fileDownload.file,
              version: params.version,
              expiredAt,
              error: null,
            });

            // state.expiredAt = expiredAt;
            // state.url = version.url;
            // update(state, 0.5);
          }
        }
      } else {
        error = res.error;
      }

      if (error) {
        // update(state, 0.3);
        throw error;
      }
    }
    // } else {
    //   state.expiredAt = oldState.expiredAt;
    //   state.url = oldState.url;
    //   update(state, 0.5);
    // }
  };

  const downloadFileUrl: FileStoreAction['downloadFileUrl'] = async (
    params,
    signal,
  ) => {
    await handlerGetFileUrl(params, signal);
  };

  const downloadFileSource: FileStoreAction['downloadFileSource'] = async (
    params,
    signal,
  ) => {
    await handlerGetFileUrl(params, signal);
  };

  const setFileUrl: FileStoreAction['setFileUrl'] = (fileId, url) => {
    set((state) => ({
      fileUrlMap: {
        ...state.fileUrlMap,
        [fileId]: url,
      },
    }));
  };

  const getFileUrl: FileStoreAction['getFileUrl'] = (fileId) => {
    return get().fileUrlMap[fileId];
  };

  const getStrokeDataFromUrl: FileStoreAction['getStrokeDataFromUrl'] = async (
    url,
  ) => {
    const res = await restGetStorkeDataFromUrl({ url });
    if (res.ok) {
      return res.data;
    }
    return undefined;
  };

  const getTranslationText: FileStoreAction['getTranslationText'] = async (
    translateString: string[],
    from: TtsLocale,
    to: TtsLocale,
  ) => {
    const translateLanguage = await restTranslateLanguage({
      inputs: translateString,
      from: from,
      to: to,
      isFree: true,
    });
    if (translateLanguage.ok) {
      const translations = translateLanguage.data.translations;
      return translations.map((translate) => translate.output);
    }
    return undefined;
  };

  const getTtsAudios: FileStoreAction['getTtsAudios'] = async (
    ttsAudioString: string[],
    locale: TtsLocale,
    signal: AbortSignal,
  ) => {
    const ttsAudiosSet: Set<string> = new Set();
    const ttsAudioResult: Record<string, string> = {};
    const ttsAudioResultWithUrl: Record<
      string,
      { fileId: string; url: string }
    > = {};

    ttsAudioString.forEach((ttsAudio) => {
      ttsAudiosSet.add(ttsAudio);
    });

    for (let i = 0; i < 5; i++) {
      const ttsAudiosLocaleString: string[] = Array.from(ttsAudiosSet);

      if (ttsAudiosLocaleString.length === 0) {
        // early return
        continue;
      }

      for (let j = 0; j < ttsAudiosLocaleString.length; j += 10) {
        const itemsToProcess = ttsAudiosLocaleString.slice(j, j + 10);
        const getTtsAudiosResponse = await restGetTtsAudios({
          texts: itemsToProcess,
          locale: locale,
        });

        if (getTtsAudiosResponse.ok) {
          const ttsAudios = getTtsAudiosResponse.data.ttsAudios;

          ttsAudios.forEach((item) => {
            if (item !== null) {
              ttsAudioResult[item.text] = item.fileId;

              ttsAudiosSet.delete(item.text);
            }
          });
        }
      }
      await new Promise((resolve) =>
        setTimeout(resolve, Math.pow(2, i) * 1000),
      );
    }

    await each(
      Object.entries(ttsAudioResult),
      asyncify(async ([text, fileId]: [text: string, fileId: string]) => {
        await downloadFileUrl(
          { fileType: FileType.TtsAudio, fileId: fileId },
          signal,
        );

        const url = getFileDownload(
          `${FileType.TtsAudio}_${fileId}`,
        )?.getVersion()?.url;

        if (url) {
          ttsAudioResultWithUrl[text] = { fileId: fileId, url: url };
        }
      }),
    );

    return ttsAudioResultWithUrl;
  };

  return {
    initialize,
    shutdown,
    fileDownload: {},
    fileUrlMap: {}, // Initialize the map
    updateFileDownload,
    getFileDownload,
    downloadFileSource,
    downloadFileUrl,
    setFileUrl,
    getFileUrl,
    getStrokeDataFromUrl,
    getTranslationText,
    getTtsAudios,
  };
};

export const useFileStore = create<Store>()(initializer);
